<template>
  <div class="import">
    <div class="import-holder" v-if="canEdit">
      <card :title="$t('recently_uploaded_files')" id="recently-uploaded">
        <file-list ref="importFileList" :data="recently_uploaded_files" @importValidatedDataEvent="importValidatedData"
          @live-operation-finished="getUploadedFiles(company)"></file-list>
      </card>

      <card
        :title="$t('common.upload_file') + ' ' + $t('allowed_extensions') + ' ' + $t('allowed_extensions_for_shape')"
        :title-font-size="18"
        container-class="p-3"
        id="file-uploader"
        class="mt-3 pl-0"
      >
        <file-uploader
          ref="file_uploader"
          :config="uploadConfig"
          :params="requestParams"
          @onFileSelected="onFileSelected"
          @onFileUploaded="onFileUploaded"
          @onFileUploadError="onFileUploadError"
        ></file-uploader>
      </card>
      <import-department-selector
        ref="importDepartmentSelector"
        :show="showDepartmentSelector"
        @upload="persistFileUpload"
        @cancel="cancelDepartmentSelector"
      ></import-department-selector>
      <ConfirmDxPopup
        :show="showConfirmationDialog"
        @close="cancelValidatedDataImport"
        @confirm="startValidatedDataImport"
        :title="$t('tools.import.transfer_to_live_data')"
        :message="$t('tools.import_validator.transfer_to_live_data_question')"
      ></ConfirmDxPopup>
    </div>
    <div v-else>
      <forbidden></forbidden>
    </div>
  </div>
</template>

<script>
import FileList from "./partials/FileList";
import ImportDepartmentSelector from "./partials/ImportDepartmentSelector";
import ConfirmDxPopup from "@/components/Modals/ConfirmDxPopup.vue";
import FileUploader from "../../../modules/FileUploader/FileUploader.vue";
import { generateUUID } from "@/helpers/common";
import { canEdit, permissions, permissionTypes } from "@/helpers/Permissions";
import Forbidden from "@/pages/errors/Forbidden";
import * as api from "../../../store/api/endpoints";
import { mapActions, mapGetters } from "vuex";
import Cookies from "js-cookie";

export default {
  name: "Import",
  data() {
    return {
      recently_uploaded_files: this.recentlyUploadedFiles ? this.recentlyUploadedFiles : [],
      showDepartmentSelector: false,
      selectedDepartmentUUID: null,
      warehouseUUID: null,
      company: null,
      uploadConfig: {},
      requestParams: {},
      intervalId: null, // Store the interval ID here
      showConfirmationDialog: false,
      transferData: null,
      file: null,
    };
  },
  computed: {
    ...mapGetters({
      recentlyUploadedFiles: "company/recentlyUploadedFiles",
    }),
    config() {
      return {
        permission: permissions.IMPORT,
        permission_type: permissionTypes.module,
      };
    },
    canEdit() {
      return canEdit(this);
    },
    sessionID() {
      return generateUUID();
    },
  },
  components: {
    FileUploader,
    FileList,
    ImportDepartmentSelector,
    Forbidden,
    ConfirmDxPopup,
  },
  methods: {
    ...mapActions({
      getRecentlyUploadedFiles: "company/getRecentlyUploadedFiles",
    }),
    onFileUploaded(file) {
      this.getUploadedFiles();
    },
    onFileUploadError(file) {
      this.getUploadedFiles();
    },
    onFileSelected(file) {
      this.selectedDepartmentUUID = null;
      this.$refs.importDepartmentSelector.reset();
      this.showDepartmentSelector = true;
    },
    getUploadedFiles(company) {
      if (company && company.uuid) {
        this.getRecentlyUploadedFiles({
          company_uuid: company.uuid,
        });
      }
    },
    persistFileUpload(data) {
      this.showDepartmentSelector = false;
      this.selectedDepartmentUUID = data.department_uuid;
      this.warehouseUUID = data.warehouse_uuid;
      this.$refs.file_uploader.upload(data);
    },
    cancelDepartmentSelector() {
      this.$refs.file_uploader.clearSelectedFiles();
      this.showDepartmentSelector = false;
      this.$refs.importFileList.setItemToLiveData(null);
    },
    setUploadConfig(company) {
      this.uploadConfig = {
        upload_url: api.replaceParams(api.FILE_UPLOAD_URL, {
          company_uuid: company.uuid,
          session_id: this.sessionID,
          warehouse_uuid: this.warehouseUUID,
        }),
        allowed_extensions: ".zip,.csv,.xls,.xlsx,.txt",
        max_file_size_mb: 10,
        forbidden_extensions: [".exe", ".bat", ".cmd", ".msi", ".js", ".vbs", ".ps1", ".scr", ".jar", ".com"],
      };
    },
    importValidatedData(data) {
      this.transferData = data;
      this.showConfirmationDialog = true;
    },
    cancelValidatedDataImport() {
      this.showConfirmationDialog = false;
      this.transferData = null;
      this.$refs.importFileList.setItemToLiveData(null);
    },
    startValidatedDataImport() {
      this.showConfirmationDialog = false;
      if (!this.transferData) {
        return;
      }

      const transfer_settings = {
        import_id: this.transferData.import_id,
        import_type: this.transferData.import_type,
        department_uuid: this.transferData.department_uuid,
        warehouse_uuid: this.transferData.warehouse_uuid,
        season_uuid: this.transferData.season_uuid,
        source_data_type: this.transferData.source_data_type,
        uploaded_file_id: this.transferData.uploaded_file_id,
        session_id: this.transferData.session_id,
        filename: this.transferData.filename,
        additional_data: this.transferData.additional_data,
      };

      this.$refs.importFileList.startLiveOperation("transfer", transfer_settings);
      this.transferData = null;
    },
  },
  async mounted() {
    let vm = this;
    const dom = await this.$store.dispatch("auth/checkDomain", {
      domain: Cookies.get("domain"),
    });
    this.company = dom;
    this.setUploadConfig(dom);
    vm.getUploadedFiles(dom);
    this.intervalId = setInterval(function () {
      vm.getUploadedFiles(vm.company);
    }, 8000);
  },
  beforeDestroy() {
    // Use beforeUnmount() for Vue 3
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  },
  watch: {
    recentlyUploadedFiles: {
      handler: function (newVal) {
        this.recently_uploaded_files = newVal;
      },
      deep: true,
    },
  },
};
</script>
